import {type AuthUser, useLandingAuth} from "@/state/authStore";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

interface Options extends Omit<RequestInit, "body"> {
  json?: unknown;
  auth?: boolean;
  skipAuthRefresh?: boolean;
}

async function rawFetch(path: string, opts: Options = {}): Promise<Response> {
  const headers = new Headers(opts.headers);
  headers.set("Accept", "application/json");
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (opts.auth !== false) {
    const token = useLandingAuth.getState().accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers,
    credentials: "include",
    body: opts.json !== undefined ? JSON.stringify(opts.json) : undefined,
  });
}

let refreshInflight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInflight) {
    return refreshInflight;
  }
  const { refreshToken } = useLandingAuth.getState();
  if (!refreshToken) {
    return false;
  }
  refreshInflight = (async () => {
    try {
      const res = await rawFetch("/auth/refresh", {
        method: "POST",
        json: { refresh_token: refreshToken },
        auth: false,
      });
      if (!res.ok) {
        return false;
      }
      const data = (await res.json()) as {
        access_token: string;
        refresh_token: string;
        user: AuthUserApi;
      };
      useLandingAuth.getState().setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        user: mapUser(data.user),
      });
      return true;
    } catch {
      return false;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

interface AuthUserApi {
  id: string;
  telegram_id: number;
  telegram_username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  photo_url?: string | null;
  language_code: string;
  role?: string;
  email?: string | null;
  phone_number?: string | null;
  referrer_display_name?: string | null;
  has_referral_binding?: boolean;
}

function mapUser(user: AuthUserApi) {
  const current = useLandingAuth.getState().user;
  return {
    id: user.id,
    telegram_id: user.telegram_id,
    telegram_username: user.telegram_username,
    first_name: user.first_name,
    last_name: user.last_name,
    photo_url:
      user.photo_url?.trim() ||
      current?.photo_url ||
      null,
    language_code: user.language_code,
    role: user.role as AuthUser["role"],
    email: user.email,
    phone_number: user.phone_number,
    referrer_display_name:
      user.referrer_display_name ?? current?.referrer_display_name,
    has_referral_binding:
      user.has_referral_binding ?? current?.has_referral_binding,
  };
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  let res = await rawFetch(path, opts);
  if (res.status === 401 && opts.auth !== false && !opts.skipAuthRefresh) {
    if (await tryRefresh()) {
      res = await rawFetch(path, opts);
    } else {
      useLandingAuth.getState().clear();
    }
  }
  if (!res.ok) {
    let code = "http_error";
    let message = res.statusText;
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string };
      };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
